import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { Save, Search } from "lucide-react";

const AdminSeo = () => {
  const { lang } = useI18n();
  const [seo, setSeo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiClient.get("/admin/settings").then((r) => setSeo(r.data.seo || {})); }, []);

  if (!seo) return <div className="text-ink-muted">Loading…</div>;
  const upd = (k, v) => setSeo({ ...seo, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put("/admin/settings", { seo });
      toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-seo-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">SEO</h1>
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="seo-save"><Save className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}</button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Meta tags — Nederlands" : "Meta tags — Dutch"}</div>
          <F label="Meta title NL" value={seo.meta_title_nl} onChange={(v) => upd("meta_title_nl", v)} testid="seo-title-nl" max={60} />
          <T label="Meta description NL" value={seo.meta_description_nl} onChange={(v) => upd("meta_description_nl", v)} testid="seo-desc-nl" max={160} />
        </div>
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">Meta tags — English</div>
          <F label="Meta title EN" value={seo.meta_title_en} onChange={(v) => upd("meta_title_en", v)} testid="seo-title-en" max={60} />
          <T label="Meta description EN" value={seo.meta_description_en} onChange={(v) => upd("meta_description_en", v)} testid="seo-desc-en" max={160} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">Open Graph</div>
          <F label="OG image URL (1200×630 recommended)" value={seo.og_image} onChange={(v) => upd("og_image", v)} testid="seo-og-image" />
          {seo.og_image && <div className="aspect-[1200/630] bg-secondary-warm overflow-hidden max-w-md"><img src={seo.og_image} alt="" className="w-full h-full object-cover" /></div>}
        </div>
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Analytics & Robots" : "Analytics & Robots"}</div>
          <F label="Google Analytics 4 ID (e.g., G-XXXXXXXXXX)" value={seo.ga4_measurement_id} onChange={(v) => upd("ga4_measurement_id", v)} testid="seo-ga4" />
          <F label="Facebook Pixel ID" value={seo.facebook_pixel_id} onChange={(v) => upd("facebook_pixel_id", v)} testid="seo-fb-pixel" />
          <label className="block">
            <div className="text-xs font-label text-ink-muted mb-1">robots meta</div>
            <select value={seo.robots} onChange={(e) => upd("robots", e.target.value)} className="border border-black/15 bg-white px-3 py-2 text-sm w-full" data-testid="seo-robots">
              <option value="index, follow">index, follow</option>
              <option value="noindex, nofollow">noindex, nofollow</option>
              <option value="index, nofollow">index, nofollow</option>
              <option value="noindex, follow">noindex, follow</option>
            </select>
          </label>
        </div>
      </div>

      <div className="mt-6 border border-black/10 bg-white p-6">
        <div className="font-label text-ink-muted mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> {lang === "nl" ? "Voorbeeld in Google" : "Google preview"}</div>
        <div className="max-w-xl">
          <div className="text-[#1a0dab] text-lg leading-tight truncate">{lang === "nl" ? seo.meta_title_nl : seo.meta_title_en}</div>
          <div className="text-[#006621] text-xs mt-0.5">https://snapshop.it.com</div>
          <div className="text-sm text-ink-muted mt-1 line-clamp-2">{lang === "nl" ? seo.meta_description_nl : seo.meta_description_en}</div>
        </div>
      </div>
    </div>
  );
};

const F = ({ label, value, onChange, testid, max }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1 flex items-center justify-between">
      <span>{label}</span>
      {max && <span className={value && value.length > max ? "text-terracotta" : ""}>{(value || "").length}/{max}</span>}
    </div>
    <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);
const T = ({ label, value, onChange, testid, max }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1 flex items-center justify-between">
      <span>{label}</span>
      {max && <span className={value && value.length > max ? "text-terracotta" : ""}>{(value || "").length}/{max}</span>}
    </div>
    <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);

export default AdminSeo;
